import {
  AVAILABLE_MODELS,
  DEFAULT_MODEL_ID,
  ModelConfig,
  normalizeReasoningEffort,
  ReasoningEffort,
  resolveStoredModelId,
} from "@config";
import { type Assistant, Client, type Thread } from "@langchain/langgraph-sdk";
import { makeAutoObservable } from "mobx";
import {
  flattenAgentThreads,
  getAgentThreadOptions,
  getAgentThreadsOptions,
  getRoadmapThreadOptions,
} from "src/hooks/queries/agent-threads";
import type { AssistantAccessConfig } from "src/utils/assistantAccess";
import { queryClient } from "src/utils/queryClient";
import { authHelper } from "./helpers/auth";
import { RootStoreType } from "./index";

export type AgentAssistant = Assistant;
export type AgentThread = Thread;
export type AgentRunCreatePayload = NonNullable<
  Parameters<Client["runs"]["create"]>[2]
>;
export type ActiveTiptapDocumentContext = {
  documentId: string;
};
export type AgentThreadMetadataUpdate = { name: string } & Record<
  string,
  unknown
>;

const SELECTED_MODEL_CONFIG_STORAGE_KEY = "selectedModelConfig";
const SELECTED_REASONING_EFFORT_STORAGE_KEY = "selectedReasoningEffort";
const SELECTED_MODEL_CONFIG_VERSION_STORAGE_KEY = "selectedModelConfigVersion";
const SELECTED_MODEL_CONFIG_VERSION = "gpt-6.1-sol-high-default-reset-v2";
const SELECTED_ASSISTANT_ID_STORAGE_KEY = "selectedAssistantId";

function isReasoningEffort(value: unknown): value is ReasoningEffort {
  return (
    value === "none" ||
    value === "low" ||
    value === "medium" ||
    value === "high" ||
    value === "xhigh" ||
    value === "max"
  );
}

export class AgentStore {
  rootStore;

  private _client: Client;

  // Current thread ID being used
  private _currentThreadId: string | null = null;

  // Error management
  private _error: any = null;

  // Assistant management
  private _availableAssistants: Assistant[] = [];
  private _assistantsLoading: boolean = false;
  private _selectedAssistantId: string | null = null;

  // Model configuration management
  private _selectedModelConfig: ModelConfig | null = null;
  private _selectedReasoningEffort: ReasoningEffort | undefined = undefined;
  private _activeTiptapDocumentContext: ActiveTiptapDocumentContext | null =
    null;

  constructor(rootStore: RootStoreType) {
    makeAutoObservable(this, { rootStore: false }, { autoBind: true });
    this.rootStore = rootStore;

    // Initialize selected assistant ID with environment default
    this._selectedAssistantId = window._env_.ASSISTANT_ID || null;

    // Initialize model configuration from localStorage
    this.initializeModelConfiguration();

    this._client = new Client({
      apiKey: window._env_.ASSISTANT_API_KEY,
      apiUrl: window._env_.ASSISTANT_API_URL,
      streamProtocol: "v2",
      onRequest: this.addAuthHeaders,
    });
  }

  private async addAuthHeaders(_url: URL, init: RequestInit) {
    // Always get a fresh token for each request. The LangGraph v2 stream
    // transport bypasses callerOptions.fetch, but it does invoke onRequest.
    const token = await authHelper.getAuth0Functions().getAccessTokenSilently();

    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);
    headers.set("X-Organization-ID", this.orgId);

    return {
      ...init,
      headers,
    };
  }

  get orgId() {
    const url = new URL(window.location.href);
    const pathSegments = url.pathname.split("/").filter(Boolean);
    return pathSegments[0] || "";
  }

  get currentThreadId() {
    return this._currentThreadId;
  }

  set currentThreadId(threadId: string | null) {
    if (threadId !== this._currentThreadId) {
      this._currentThreadId = threadId;

      // Update URL if we're in a browser environment
      if (typeof window !== "undefined") {
        this.updateUrlParams(threadId);
      }
    }
  }

  get activeTiptapDocumentContext() {
    return this._activeTiptapDocumentContext;
  }

  setActiveTiptapDocumentContext(context: ActiveTiptapDocumentContext) {
    this._activeTiptapDocumentContext = context;
  }

  clearActiveTiptapDocumentContext(documentId?: string) {
    if (
      documentId &&
      this._activeTiptapDocumentContext?.documentId !== documentId
    ) {
      return;
    }

    this._activeTiptapDocumentContext = null;
  }

  // Assistant getters
  get availableAssistants() {
    return [...this._availableAssistants];
  }

  get assistantsLoading() {
    return this._assistantsLoading;
  }

  get selectedAssistantId() {
    return this._selectedAssistantId;
  }

  get selectedAssistant() {
    if (!this._selectedAssistantId) {
      return undefined;
    }
    return this._availableAssistants.find(
      (a) => a.assistant_id === this._selectedAssistantId
    );
  }

  // Initialize from URL params (call this on app startup)
  initializeFromUrl() {
    if (typeof window !== "undefined") {
      try {
        const url = new URL(window.location.href);
        const threadIdFromUrl = url.searchParams.get("agentThreadId");
        if (threadIdFromUrl) {
          this._currentThreadId = threadIdFromUrl;
        }
      } catch (error) {
        console.warn("Failed to initialize from URL:", error);
      }
    }
  }

  private updateUrlParams(threadId: string | null) {
    try {
      const url = new URL(window.location.href);
      if (threadId) {
        url.searchParams.set("agentThreadId", threadId);
      } else {
        url.searchParams.delete("agentThreadId");
      }

      // Use replace to avoid adding to browser history
      window.history.replaceState({}, "", url.toString());
    } catch (error) {
      console.warn("Failed to update URL params:", error);
    }
  }

  // Navigate to a specific thread
  navigateToThread(threadId: string | null) {
    this.setError(null);
    if (threadId === this.currentThreadId) this.updateUrlParams(threadId);
    this.currentThreadId = threadId;
  }

  // Create a new thread (clears current thread ID)
  async createNewThread(
    orgId: string,
    userId: string,
    metadata?: Record<string, unknown>
  ): Promise<string> {
    const thread = await this._client.threads.create({
      metadata: {
        organization_id: orgId,
        user_id: userId,
        ...metadata,
      },
    });
    this.currentThreadId = thread.thread_id;
    return thread.thread_id;
  }

  async createBackgroundRun(threadId: string, payload: AgentRunCreatePayload) {
    const assistantId = window._env_.ASSISTANT_ID;
    if (!assistantId) throw new Error("Assistant ID is required");

    try {
      return await this._client.runs.create(threadId, assistantId, payload);
    } catch (error) {
      console.error(error);
    }
  }

  async updateThread(
    threadId: string,
    metadata: AgentThreadMetadataUpdate
  ): Promise<Thread> {
    const orgId = this.orgId;
    const userId = await authHelper.getUserId();
    const thread = await this._client.threads.update(threadId, {
      metadata: { ...metadata, organization_id: orgId, user_id: userId },
    });
    queryClient.setQueryData(
      getAgentThreadOptions(this._client, orgId, threadId).queryKey,
      thread
    );
    await queryClient.invalidateQueries({
      queryKey: getAgentThreadsOptions(this._client, orgId, userId).queryKey,
    });
    return thread;
  }

  setError(error: any) {
    this._error = error;
  }

  get error() {
    return this._error;
  }

  // Clear all thread data
  clearThreadData() {
    this._currentThreadId = null;
    this.updateUrlParams(null);
    this._error = null;
  }

  get client() {
    return this._client;
  }

  get selectedModelConfig(): ModelConfig {
    if (!this._selectedModelConfig) {
      this.initializeModelConfiguration();
    }
    return this._selectedModelConfig!;
  }

  get selectedReasoningEffort(): ReasoningEffort | undefined {
    return this._selectedReasoningEffort;
  }

  private initializeModelConfiguration() {
    if (
      localStorage.getItem(SELECTED_MODEL_CONFIG_VERSION_STORAGE_KEY) !==
      SELECTED_MODEL_CONFIG_VERSION
    ) {
      const defaultModelConfig = AVAILABLE_MODELS[DEFAULT_MODEL_ID];
      this.applyModelConfiguration(defaultModelConfig);
      this.persistModelConfiguration(defaultModelConfig);
      this.resetReasoningEffortToModelDefault();
      localStorage.setItem(
        SELECTED_MODEL_CONFIG_VERSION_STORAGE_KEY,
        SELECTED_MODEL_CONFIG_VERSION
      );
      return;
    }

    this.applyModelConfiguration(this.getStoredModelConfiguration());
    this.initializeReasoningEffort();
  }

  private getStoredModelConfiguration(): ModelConfig {
    const modelConfig = localStorage.getItem(SELECTED_MODEL_CONFIG_STORAGE_KEY);

    if (!modelConfig) {
      return AVAILABLE_MODELS[DEFAULT_MODEL_ID];
    }

    try {
      const parsed: unknown = JSON.parse(modelConfig);
      if (typeof parsed === "object" && parsed !== null) {
        const storedModelId = Reflect.get(parsed, "id");
        if (typeof storedModelId === "string") {
          const resolvedModelId = resolveStoredModelId(storedModelId);
          if (!resolvedModelId) {
            throw new Error("Stored model ID is no longer supported");
          }
          const selectedModelConfig = AVAILABLE_MODELS[resolvedModelId];

          if (resolvedModelId !== storedModelId) {
            this.persistModelConfiguration(selectedModelConfig);
          }

          return selectedModelConfig;
        }
      }
    } catch {
      // Fall through to the default model and clear invalid persisted state.
    }

    localStorage.removeItem(SELECTED_MODEL_CONFIG_STORAGE_KEY);
    localStorage.removeItem(SELECTED_REASONING_EFFORT_STORAGE_KEY);
    return AVAILABLE_MODELS[DEFAULT_MODEL_ID];
  }

  private initializeReasoningEffort() {
    const defaultReasoningEffort = this._selectedModelConfig?.reasoningEffort;
    if (!defaultReasoningEffort) {
      this._selectedReasoningEffort = undefined;
      localStorage.removeItem(SELECTED_REASONING_EFFORT_STORAGE_KEY);
      return;
    }

    const reasoningEffort = localStorage.getItem(
      SELECTED_REASONING_EFFORT_STORAGE_KEY
    );
    if (!reasoningEffort) {
      this._selectedReasoningEffort = defaultReasoningEffort;
      return;
    }

    try {
      const parsedReasoningEffort: unknown = JSON.parse(reasoningEffort);
      if (isReasoningEffort(parsedReasoningEffort)) {
        const normalizedReasoningEffort = normalizeReasoningEffort(
          this._selectedModelConfig!,
          parsedReasoningEffort
        );
        if (normalizedReasoningEffort === parsedReasoningEffort) {
          this._selectedReasoningEffort = parsedReasoningEffort;
          return;
        }
      }
    } catch {
      // Fall through to the default reasoning effort and overwrite invalid storage.
    }

    this._selectedReasoningEffort = defaultReasoningEffort;
    localStorage.setItem(
      SELECTED_REASONING_EFFORT_STORAGE_KEY,
      JSON.stringify(defaultReasoningEffort)
    );
  }

  private applyModelConfiguration(modelConfig: ModelConfig) {
    this._selectedModelConfig = modelConfig;
  }

  private persistModelConfiguration(modelConfig: ModelConfig) {
    localStorage.setItem(
      SELECTED_MODEL_CONFIG_STORAGE_KEY,
      JSON.stringify(modelConfig)
    );
  }

  private resetReasoningEffortToModelDefault() {
    const defaultReasoningEffort = this._selectedModelConfig?.reasoningEffort;

    if (defaultReasoningEffort) {
      this._selectedReasoningEffort = defaultReasoningEffort;
      localStorage.setItem(
        SELECTED_REASONING_EFFORT_STORAGE_KEY,
        JSON.stringify(defaultReasoningEffort)
      );
      return;
    }

    this._selectedReasoningEffort = undefined;
    localStorage.removeItem(SELECTED_REASONING_EFFORT_STORAGE_KEY);
  }

  setSelectedModelConfig(modelConfig: ModelConfig) {
    this._selectedModelConfig = modelConfig;
    this.persistModelConfiguration(modelConfig);

    if (modelConfig.reasoningEffort) {
      this._selectedReasoningEffort = modelConfig.reasoningEffort;
      localStorage.setItem(
        SELECTED_REASONING_EFFORT_STORAGE_KEY,
        JSON.stringify(modelConfig.reasoningEffort)
      );
    } else {
      this._selectedReasoningEffort = undefined;
      localStorage.removeItem(SELECTED_REASONING_EFFORT_STORAGE_KEY);
    }
  }

  setSelectedReasoningEffort(reasoningEffort: ReasoningEffort) {
    const normalizedReasoningEffort = normalizeReasoningEffort(
      this.selectedModelConfig,
      reasoningEffort
    );
    this._selectedReasoningEffort = normalizedReasoningEffort;

    if (normalizedReasoningEffort) {
      localStorage.setItem(
        SELECTED_REASONING_EFFORT_STORAGE_KEY,
        JSON.stringify(normalizedReasoningEffort)
      );
      return;
    }

    localStorage.removeItem(SELECTED_REASONING_EFFORT_STORAGE_KEY);
  }

  async getRoadmapThread(taskContext: string) {
    const orgId = this.orgId;
    const userId = await authHelper.getUserId();
    return queryClient.fetchQuery(
      getRoadmapThreadOptions(this._client, orgId, userId, taskContext)
    );
  }

  async refreshThreads(): Promise<Thread[]> {
    const orgId = this.orgId;
    const userId = await authHelper.getUserId();
    const options = getAgentThreadsOptions(this._client, orgId, userId);
    await queryClient.invalidateQueries({
      queryKey: options.queryKey,
      refetchType: "none",
    });
    const data = await queryClient.fetchInfiniteQuery(options);
    return flattenAgentThreads(data.pages);
  }

  // Assistant methods
  setAssistantsLoading(loading: boolean) {
    this._assistantsLoading = loading;
  }

  setAvailableAssistants(assistants: Assistant[]) {
    this._availableAssistants = assistants;
  }

  setSelectedAssistantId(assistantId: string | null) {
    this._selectedAssistantId = assistantId;
    // Store in localStorage for persistence
    if (assistantId) {
      localStorage.setItem(SELECTED_ASSISTANT_ID_STORAGE_KEY, assistantId);
    } else {
      localStorage.removeItem(SELECTED_ASSISTANT_ID_STORAGE_KEY);
    }
  }

  // Fetch available assistants from API
  private filterAssistantsByAccessConfig(
    assistants: Assistant[],
    accessConfig?: AssistantAccessConfig
  ) {
    if (!accessConfig || accessConfig.assistantIds === null) {
      return assistants;
    }

    const assistantIds = new Set(accessConfig.assistantIds);
    const filteredAssistants = assistants.filter((assistant) =>
      assistantIds.has(assistant.assistant_id)
    );
    if (filteredAssistants.length > 0) {
      return filteredAssistants;
    }

    const environmentAssistantId = window._env_.ASSISTANT_ID || null;
    const environmentAssistant = assistants.find(
      (assistant) => assistant.assistant_id === environmentAssistantId
    );

    return environmentAssistant ? [environmentAssistant] : filteredAssistants;
  }

  private ensureSelectedAssistantIsAvailable(
    assistants: Assistant[],
    accessConfig?: AssistantAccessConfig
  ) {
    if (
      this._selectedAssistantId &&
      assistants.some(
        (assistant) => assistant.assistant_id === this._selectedAssistantId
      )
    ) {
      return;
    }

    const defaultAssistantId = accessConfig?.defaultAssistantId;
    const environmentAssistantId = window._env_.ASSISTANT_ID || null;
    const nextAssistantId =
      assistants.find(
        (assistant) => assistant.assistant_id === defaultAssistantId
      )?.assistant_id ||
      assistants.find(
        (assistant) => assistant.assistant_id === environmentAssistantId
      )?.assistant_id ||
      assistants[0]?.assistant_id ||
      null;

    this.setSelectedAssistantId(nextAssistantId);
  }

  async getAvailableAssistants(
    accessConfig?: AssistantAccessConfig
  ): Promise<Assistant[]> {
    try {
      this.setAssistantsLoading(true);
      const assistants = await this._client.assistants.search({
        limit: 100, // Get all assistants, adjust limit as needed
        offset: 0,
        sortBy: "name",
        sortOrder: "asc",
      });
      const availableAssistants = this.filterAssistantsByAccessConfig(
        assistants,
        accessConfig
      );
      this.setAvailableAssistants(availableAssistants);
      this.ensureSelectedAssistantIsAvailable(
        availableAssistants,
        accessConfig
      );
      return availableAssistants;
    } catch (error) {
      console.error("Failed to fetch assistants:", error);
      return [];
    } finally {
      this.setAssistantsLoading(false);
    }
  }

  // Initialize assistant selection from localStorage or environment
  initializeAssistantSelection() {
    const storedAssistantId = localStorage.getItem(
      SELECTED_ASSISTANT_ID_STORAGE_KEY
    );
    if (storedAssistantId) {
      this._selectedAssistantId = storedAssistantId;
    } else {
      this._selectedAssistantId = window._env_.ASSISTANT_ID || null;
    }
  }
}
